<template>
    <v-row :dense="dense">
        <v-col v-bind="zipCol">
            <LabelField :label="$t('plz')">
                <v-combobox
                    v-model="zipSelect"
                    :loading="zipLoading"
                    :search-input.sync="zipSearch"
                    :items="zipCollection.models"
                    item-text="zipString"
                    item-value="id"
                    return-object
                    dense
                    outlined
                    hide-details="auto"
                    hide-no-data
                    :rules="[zipFieldRequired(record) ? rules.required : null, zipBetween].filter(Boolean)"
                    @input="onZipInput"
                >
                    <template #item="{ item, on, attrs }">
                        <v-list-item-content v-bind="attrs" v-on="on">
                            <v-list-item-title> {{ item.zipString }}, {{ item.city_long }} </v-list-item-title>
                        </v-list-item-content>
                    </template>
                </v-combobox>
            </LabelField>
        </v-col>
        <v-col v-bind="cityCol">
            <LabelField :label="$t('ort')">
                <v-combobox
                    v-model="citySelect"
                    :loading="cityLoading"
                    :search-input.sync="citySearch"
                    :items="cityZipCollection.models"
                    item-text="city_long"
                    item-value="id"
                    return-object
                    dense
                    outlined
                    hide-details="auto"
                    hide-no-data
                    :rules="[cityFieldRequired(record) ? rules.required : null, cityMaxLength].filter(Boolean)"
                    @input="onCityInput"
                >
                    <template #item="{ item, on, attrs }">
                        <v-list-item-content v-bind="attrs" v-on="on">
                            <v-list-item-title> {{ item.zipString }}, {{ item.city_long }} </v-list-item-title>
                        </v-list-item-content>
                    </template>
                </v-combobox>
            </LabelField>
        </v-col>
    </v-row>
</template>

<script>
import { debounce, formRules } from '@/helpers/tools';
import { Address } from '@/models/Address';
import { Zip, ZipCollection } from '@/models/Zip';

export default {
    name: 'AddressZipCityGroup',
    props: {
        record: {
            type: Address,
            required: true,
        },
        zipCol: {
            type: Object,
            default: () => {
                return {
                    cols: '12',
                    lg: '4',
                };
            },
        },
        cityCol: {
            type: Object,
            default: () => {
                return {
                    cols: '12',
                    lg: '8',
                };
            },
        },
        dense: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['zipChange', 'cityChange', 'changeRecord'],

    data() {
        return {
            zipLoading: false,
            zipSelect: null,
            zipSearch: null,
            zipCollection: new ZipCollection(),

            cityLoading: false,
            citySelect: null,
            citySearch: null,
            cityZipCollection: new ZipCollection(),

            zipInit: true,
            cityInit: true,
        };
    },

    watch: {
        zipSearch(newValue, oldVal) {
            newValue && newValue !== oldVal && newValue.length >= 3 && this.searchZip(newValue);
        },
        zipSelect(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.record?.set('zip', newVal);
                this.changeRecord();
            }
        },
        citySearch(newValue, oldVal) {
            newValue && newValue !== oldVal && this.searchCity(newValue);
        },
        citySelect(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.record?.set('city', newVal);
                this.changeRecord();
            }
        },
        record(newVal, oldVal) {
            if (newVal) {
                this.zipSelect = newVal?.zip;
                this.citySelect = newVal?.city;
            }
        },
    },

    created() {
        this.rules = formRules;
    },

    mounted() {
        this.zipSelect = this.record?.zip;
        this.citySelect = this.record?.city;
    },

    methods: {
        onZipInput(value) {
            if (value instanceof Zip) {
                this.record?.set('zip', value.zipString);
                this.updateCityZipSelect(value);
                this.updateRecCountryId(value);
                this.$emit('select', value);
            } else if (typeof value === 'string') {
                this.record?.set('zip', value);
            }
        },
        onCityInput(value) {
            if (value instanceof Zip) {
                this.record?.set('city', value.city_long);
                this.updateCityZipSelect(value);
                this.updateRecCountryId(value);
                this.$emit('select', value);
            } else if (typeof value == 'string') {
                this.record?.set('city', value);
            }
        },

        updateCityZipSelect(zipObj) {
            this.zipSelect = zipObj.zipString;
            this.citySelect = zipObj.city_long;
        },

        async searchZip(value) {
            // prevent to load list data if not needed yet. data will be loaded for first change.
            if (this.zipInit) {
                this.zipInit = false;
                return;
            }
            this.zipLoading = true;
            await this.zipCollection.setFilter('zip', value, 'CONTAINS').query();
            this.zipLoading = false;
        },
        async searchCity(value) {
            // prevent to load list data if not needed yet. data will be loaded for first change.
            if (this.cityInit) {
                this.cityInit = false;
                return;
            }
            this.cityLoading = true;
            await this.cityZipCollection.setFilter('city_long', value, 'CONTAINS').query();
            this.cityLoading = false;
        },

        zipFieldRequired(address) {
            // ZIP is only required for locations:
            if (address?.type === 'location') {
                return true;
            } else {
                return false;
            }
        },
        cityFieldRequired(address) {
            // city is only required for locations:
            if (address?.type === 'location') {
                return true;
            } else {
                return false;
            }
        },

        zipBetween(value) {
            if (value instanceof Zip) {
                value = Number(value.zipString);
            }
            return this.rules.between(1000, 99999)(value);
        },
        cityMaxLength(value) {
            if (value instanceof Zip) {
                value = value.city_long;
            }
            return this.rules.maxLength(100)(value);
        },

        updateRecCountryId: debounce(function (val) {
            if (val.country_id) {
                this.record.set('country_id', val.country_id);
            }
        }, 200),

        changeRecord: debounce(function () {
            if (this.record) {
                this.$emit('changeRecord', this.record);
            }
        }, 200),
    },
};
</script>

<style scoped>
.flex-equal {
    padding: 0;
}
</style>
